import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { ObjectParam, StringParam, NumberParam, useQueryParams } from 'use-query-params'
import ticketSearchQuery from '../../graphql/queries/ticketsSearch'
import ticketTypesQuery from '../../graphql/queries/ticketTypes'
import QUERY_KEYS from '../../graphql/queryKeys'
import usersQuery from '../../graphql/queries/users.js'
import dayjs from '../../utilities/dayjs'
import styles from './index.module.scss'
import cn from 'classnames'
import NormalLayoutContainer from '../shared/NormalLayoutContainer'
import { HAS_RECURRING_TICKET_MESSAGE, TICKET_BILLING_STATUSES } from '../../utilities/constants'
import { PageTitle } from '../../components/page-title'
import { Spinner } from '../shared/Spinner'
import TicketsTableSearchForm from '../../components/tickets-search/TicketsTableSearchForm'
import TicketsTable from '../../components/tickets-search/TicketsTable'
import notify from '../../utilities/notify'
import { captureErrorAndNotify, handleValidateAndNotify } from '../../utilities/errorHandlers'
import { ticketsSortColumnEnumMap } from '../../graphql/enums'
import { ROUTES } from '../../routes'
import useQuery from '../../hooks/useQuery'
import useMutation from '../../hooks/useMutation'
import exportTickets from '../../graphql/mutations/exportTickets'
import resourceTypesQuery from '../../graphql/queries/resourceTypes'
import destroyTickets from '../../graphql/mutations/destroyTickets.js'
import { RECEIVE_ALL_CLIENTS_SUCCESS } from '../hauler/clients/action/clientAction.js'
import clientsAll from '../../graphql/queries/clientsAll.js'

const pageSize = 25
const yesterday = dayjs().add(-1, 'day').format('YYYY-MM-DD')

export default function TicketsSearchPage () {
  const history = useHistory()
  const dispatch = useDispatch()
  const [fakeCount, setFakeCount] = useState(null)
  const [ticketsExporting, setTicketsExporting] = useState(false)
  const { allClients, user, dataLoaders } = useSelector((
    {
      allClients,
      user: { user },
      dataLoaders
    }
  ) => ({
    allClients,
    user,
    dataLoaders
  }))

  const [queryParams, setQueryParams] = useQueryParams({
    sort: ObjectParam,
    pager: ObjectParam,
    clientId: NumberParam,
    customId: StringParam,
    dispatchSearch: StringParam,
    driverId: StringParam,
    flagged: StringParam,
    hasActiveException: StringParam,
    jobCustomId: StringParam,
    maxDate: StringParam,
    minDate: StringParam,
    resourceTypeId: StringParam,
    status: StringParam,
    ticketTypeId: StringParam,
    billingStatus: StringParam,
    city: StringParam,
    zipCode: StringParam,
    weightTicketNumber: StringParam,
    serviceFrequency: StringParam,
    recurringTicketId: StringParam
  })

  const searchParams = {
    clientId: queryParams.clientId,
    customId: queryParams.customId,
    dispatchSearch: queryParams.dispatchSearch,
    driverId: queryParams.driverId,
    flagged: queryParams.flagged,
    hasActiveException: queryParams.hasActiveException,
    jobCustomId: queryParams.jobCustomId,
    maxDate: queryParams.maxDate,
    minDate: queryParams.minDate,
    resourceTypeId: queryParams.resourceTypeId,
    status: queryParams.status,
    ticketTypeId: queryParams.ticketTypeId,
    billingStatus: queryParams.billingStatus,
    city: queryParams.city,
    zipCode: queryParams.zipCode,
    weightTicketNumber: queryParams.weightTicketNumber,
    serviceFrequency: queryParams.serviceFrequency,
    recurringTicketId: queryParams.recurringTicketId
  }

  const { data, isFetching } = useQuery([
    QUERY_KEYS.ticketsSearch,
    user.haulerId,
    25,
    queryParams.sort,
    Object.fromEntries(Object.entries(searchParams).filter(([_, v]) => v)),
    queryParams.pager
  ],
  ticketSearchQuery,
  {
    enabled: Boolean(queryParams.sort),
    onError () {
      notify('error', 'Error fetching tickets')
    }
  })

  const { data: { users: haulerTeam } = {}, isFetching: haulerTeamFetching } = useQuery([
    QUERY_KEYS.users,
    user.haulerId,
    true,
    null,
    null,
    null
  ],
  usersQuery,
  {
    onError (error) {
      captureErrorAndNotify(error, 'Unable to retrieve user list')
    },
    placeholderData: { users: { nodes: [] } }
  })

  const { data: overdueTickets } = useQuery([
    QUERY_KEYS.ticketsSearch,
    user.haulerId,
    25,
    { column: 'date', direction: 'desc' },
    { status: 'open', maxDate: yesterday }
  ],
  ticketSearchQuery,
  {
    enabled: Boolean(user.haulerId),
    onError () {
      notify('error', 'Error fetching overdue tickets')
    }
  })

  const { data: { resourceTypes = [] } = {}, isFetching: isFetchingResourceTypes } = useQuery(
    [QUERY_KEYS.resourceTypes, { haulerId: user.haulerId, status: 'ENABLED' }],
    resourceTypesQuery,
    {
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve resource types')
      }
    }
  )

  const { data: { ticketTypes }, isFetching: isLoadingTicketTypes, refetch: refetchTickets } = useQuery(
    [QUERY_KEYS.ticketTypes, { haulerId: user.haulerId, status: 'ENABLED' }],
    ticketTypesQuery,
    {
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve ticket types')
      },
      placeholderData: { ticketTypes: [] }
    }
  )

  const { mutate: exportTicketsMutation } = useMutation(exportTickets, {
    onSuccess (data) {
      const email = data?.userEmail
      window.alert(`Export Complete!  The export will be emailed to ${email} with a link to download.`)
    },
    onError () {
      notify('error', 'Error exporting tickets')
    },
    onSettled () {
      setTicketsExporting(false)
    }
  })

  function handleExportTickets () {
    if (ticketsExporting) {
      return
    }
    setTicketsExporting(true)
    return exportTicketsMutation({
      haulerId: user.haulerId,
      searchFields: searchParams,
      sortColumn: ticketsSortColumnEnumMap[queryParams.sort.column],
      sortDirection: queryParams.sort.direction.toUpperCase()
    })
  }

  function onPageRequest (direction, cursor) {
    setQueryParams({ pager: { cursor, direction, pageSize } })

    if (!fakeCount) return
    if (direction === 'before') {
      setFakeCount(prevFakeCount => ({ start: prevFakeCount.start - pageSize, end: prevFakeCount.end - pageSize }))
    }
    if (direction === 'after') {
      setFakeCount(prevFakeCount => ({ start: prevFakeCount.start + pageSize, end: prevFakeCount.end + pageSize }))
    }
  }

  function onSortChange (newSortColumn) {
    let newSortDirection = 'desc'
    if (newSortColumn === queryParams.sort.column) {
      newSortDirection = queryParams.sort.direction === 'asc' ? 'desc' : 'asc'
    }

    setFakeCount(null)
    setQueryParams({
      sort: { column: newSortColumn, direction: newSortDirection }
    })
  }

  const clearFormValues = () => {
    setFakeCount(null)
    setQueryParams({}, 'replace')
  }

  const handleTicketsSearch = (values) => {
    setFakeCount(null)
    setQueryParams({ ...nonEmptyValues(values), sort: queryParams.sort }, 'replace')
  }

  const viewOverdueTickets = () => {
    setQueryParams({ column: 'date', direction: 'desc', status: 'open', maxDate: yesterday })
  }

  const onBatchDeleteCallback = () => {
    setQueryParams({ column: 'date', direction: 'desc', status: 'open', maxDate: yesterday })
  }

  const { mutate: deleteBatchTickets } = useMutation(destroyTickets, {
    onSuccess ({ resourceImport }) {
      onBatchDeleteCallback()
      refetchTickets()
      notify('success', 'Tickets Deleted')
    },
    onError (error) {
      handleValidateAndNotify(error, 'Failed to Destroy Tickets')
    }
  })
  const onBatchDelete = (ids) => {
    // eslint-disable-next-line
    const confirmed = confirm(`Are you sure you want to delete ${ids.length} tickets? This CANNOT BE UNDONE.`);
    if (confirmed) {
      deleteBatchTickets({ ids: ids?.map((e) => ({ id: e })) })
    }
  }

  const onBatchEdit = (ids) => {
    const selectedIdsSet = new Set(ids)
    const selectedTickets = data?.ticketsSearch?.nodes?.filter(ticket => selectedIdsSet.has(ticket.id))
    const hasRecurringTicket = selectedTickets?.some(ticket => ticket.serviceFrequency === 'RECURRING')
    if (hasRecurringTicket) {
      const result = window.confirm(HAS_RECURRING_TICKET_MESSAGE)
      if (result) history.push(`${ROUTES.bulkEditTicketsPage}?ids=${ids.join(',')}`)
    } else {
      history.push(`${ROUTES.bulkEditTicketsPage}?ids=${ids.join(',')}`)
    }
  }

  const fetchAllClients = useCallback(() => {
    clientsAll({
      dataLoaders,
      queryKey: [
        QUERY_KEYS.clientsAll,
        user.haulerId,
        { column: 'name', direction: 'DESC' },
        null,
        null,
        null,
        null,
        null
      ]
    })
      .then((resp) => {
        dispatch({ type: RECEIVE_ALL_CLIENTS_SUCCESS, payload: { clients: resp.clientsAll } })
      })
      .catch((error) => {
        captureErrorAndNotify(error, 'Error fetching Accounts')
      })
  }, [user, dataLoaders, dispatch])

  useEffect(function onInitialRender () {
    fetchAllClients()
  }, [fetchAllClients])

  useEffect(function setDefaultQueryParams () {
    const defaultQueryParams = {}
    if (!queryParams.sort) {
      defaultQueryParams.sort = { column: 'date', direction: 'desc' }
    }
    setQueryParams(defaultQueryParams, 'replaceIn')
  }, [queryParams, setQueryParams])

  useEffect(function toggleFakePagination () {
    if (!data || isFetching || fakeCount !== null) return
    setFakeCount({ start: 1, end: Math.min(pageSize, data?.ticketsSearch.totalCount) })
  }, [data, isFetching, fakeCount, setFakeCount])

  const fetching = allClients.isFetching || isFetchingResourceTypes || haulerTeamFetching || isLoadingTicketTypes

  return (
    <div>
      <NormalLayoutContainer>
        <div className='container-fluid'>
          <div className={styles.titleContainer}>
            <PageTitle title='Search Tickets' />
            {overdueTickets?.ticketsSearch?.totalCount > 0 && <button
              onClick={viewOverdueTickets}
              className={cn('btn btn-primary-lt', styles.overdueTicketBtn)}>
              Overdue Tickets
            </button>}
          </div>

          <Spinner isFetching={fetching}>
            <div className='row'>
              <div className='panel panel-default'>
                <div className='panel-body'>
                  <TicketsTableSearchForm
                    clients={allClients.clients}
                    ticketTypes={ticketTypes}
                    resourceTypes={resourceTypes}
                    billingStatuses={TICKET_BILLING_STATUSES}
                    team={haulerTeam.nodes}
                    handleSubmit={handleTicketsSearch}
                    clearFormValues={clearFormValues}
                    initialFormVals={searchParams}
                    handleExportTickets={handleExportTickets}
                    ticketsExporting={ticketsExporting}
                  />
                </div>
              </div>
            </div>
          </Spinner>

          <Spinner isFetching={isFetching}>
            <div className='row push-down-super-small'>
              <div className='panel panel-default'>
                <div className='panel-body'>
                  {data?.ticketsSearch && queryParams.sort &&
                    <TicketsTable
                      tickets={data?.ticketsSearch}
                      sort={queryParams.sort}
                      onPageRequest={onPageRequest}
                      onSortChange={onSortChange}
                      fakeCount={fakeCount}
                      onBatchDelete={onBatchDelete}
                      onBatchEdit={onBatchEdit}
                      serviceFrequency={queryParams.serviceFrequency}
                    />}
                </div>
              </div>
            </div>
          </Spinner>

        </div>
      </NormalLayoutContainer>

    </div>
  )
}

function nonEmptyValues (values) {
  return Object.keys(values).reduce((acc, key) => {
    const value = values[key]
    if (value === '' || value == null) return acc
    return { ...acc, [key]: value }
  }, {})
}
